/**
 * The frame every screen hangs in: a bar across the top, and a page under it.
 *
 * WHY A BAR, AND WHY IT REPLACED A RAIL. The rail was argued for when the destinations list
 * changed length by role - a top bar's items would have slid sideways depending on who was
 * looking - and because it left the horizontal axis entirely to the content. The first
 * reason went with the internal screens; the list is two pages and a gear for everybody.
 * The second stopped being true the day this dashboard was put on a tablet at a counter: a
 * 248px column is a quarter of a 1024px screen spent on navigation that is used twice a
 * day, beside a call register that wants every pixel of width for type a person can read
 * with a spanner in their other hand. A bar spends one row, once, on the same links.
 *
 * WHAT THE BAR SAYS, LEFT TO RIGHT, is the order somebody glancing at it asks: whose
 * dashboard is this, where am I in it, what time is it there, and where do I change it. The
 * clock is the business's clock, not the reader's, for the reason `format.ts` gives at
 * length: a Sydney shop's 10:14 is 10:14 in the shop.
 *
 * THE AGENT SWITCHER IS NOT A FILTER. The agent is the first segment of the address
 * (`/eco-boats/calls`) and every link here is built under it by `useScopedTo`, so choosing
 * Eco Boats and then clicking Calls stays on Eco Boats. That is the difference between a
 * switcher and a filter, and getting it wrong is how a dashboard shows somebody one
 * business's overview and another business's calls.
 *
 * THE AGENT IS DRAWN BY HOW MANY THERE ARE RATHER THAN BY ROLE.
 * One agent is a name in the bar - a dashboard that does not say whose it is gets
 * screenshotted into the wrong email - and several is a real control. An owner with one
 * agent, which is most of them, never learns the word "agent" from this bar; an owner with
 * two switches between them exactly as we switch between organizations. That is presentation
 * only: the Worker resolves the agent on every request and 404s one the caller may not
 * have, so what this file renders cannot widen what the API will answer.
 *
 * EXACTLY ONE AGENT IS ON SCREEN AT A TIME. There used to be an "All agents" row here,
 * internal-only, and it was not a filter being cleared: it aggregated every screen across
 * the fleet, which is a view with no timezone, no currency and nobody's vocabulary - true
 * numbers under defaulted words. It is gone from the menu and gone from the server, which
 * now answers only about the agent a request names.
 *
 * NO AGENT, NO PAGE LINKS. `/agents/new` and the not-found screens are not about any agent,
 * so the bar draws the switcher and the account there and nothing that would need an agent
 * to point at.
 *
 * THE SIMPLIFIED CALLS PAGE OWNS EVERYTHING UNDER THE BAR. Every other page sits in the
 * padded, width-capped content column and scrolls with the document. A business on the
 * simplified view gets a register on `/calls` that is two panes the height of the screen,
 * each with its own scroller, so there the column is bare and the page brings its own
 * padding. It is the one route that is laid out differently by a setting rather than by
 * its own content, and the switch for it is one `useMatch` below.
 */

import { useLayoutEffect, useRef } from "react";
import { confirmLeave } from "./settings/draft";
import { NavLink, useLocation, useMatch, useNavigate, useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";
import { useSession } from "../session";
import { useCurrentAgent } from "../currentAgent";
import { cx } from "../ui";
import { Listbox, type Choice } from "./Listbox";
import { ChevronDown, Cog, Gauge, Mark, Phone, Plus, SignOut, User } from "./Icons";
import { UserMenu } from "./UserMenu";
import { useClock } from "./useClock";
import { clockDate, timeOfDay } from "../format";

/**
 * The query keys that survive a navigation: whether test calls are shown.
 *
 * Everything else - a search term, a cursor, an outcome filter - belongs to one screen and is
 * dropped when you leave it. The agent is not here: it is the path, not the query.
 */
const CARRIED = ["test"] as const;

/**
 * Build a path inside the current agent's address: `to("/calls")` is `/eco-boats/calls`, and
 * `to("/")` is the agent's overview, `/eco-boats`.
 *
 * `path` is the part under the agent, always from `/`. The test-call setting rides along, and
 * IT APPENDS RATHER THAN ASSUMES, because a caller's path may already carry a query of its own:
 * `/settings?tab=tracking` plus a bare `?test=true` would be a second `?` that every parser
 * reads as part of the tab's value.
 *
 * ONLY UNDER AN AGENT. A screen with no agent has nothing to build a link inside, and calling
 * the result there is a bug to see rather than a link to nowhere.
 */
export function useScopedTo(): (path: string) => string {
  const { current } = useCurrentAgent();
  const [params] = useSearchParams();
  const carried = new URLSearchParams();
  for (const key of CARRIED) {
    const value = params.get(key);
    if (value) carried.set(key, value);
  }
  const suffix = carried.toString();
  return (path: string) => {
    if (!current) throw new Error(`to("${path}") outside an agent's address`);
    const scoped = `/${current.slug}${path === "/" ? "" : path}`;
    if (!suffix) return scoped;
    return `${scoped}${scoped.includes("?") ? "&" : "?"}${suffix}`;
  };
}

/*
 * UNDER 900px THE BAR WRAPS TO TWO ROWS: the business and the right-hand cluster on the
 * first, the page links across the second. Nothing is hidden that a phone needs - the
 * clock drops its date and keeps its time. The account is an avatar at every width.
 *
 * THE BOOT SKELETON IN `App.tsx` DRAWS THIS SHELL A SECOND TIME - the same bar height, the
 * same content column. Any geometry changed here has to be changed there too, or the page
 * moves under the reader on first paint.
 *
 * `z-40` PUTS IT ABOVE `PageHead` (`z-30`) AND BELOW `Dialog` (`z-50`). The head is sticky
 * too, directly under this, and the agent menu drops out of this bar over it: at `z-30`
 * or lower the menu would open underneath the page's own title.
 */
const BAR = "sticky top-0 z-40 border-b border-line bg-paper";
const BAR_ROW =
  "flex min-h-16 items-center gap-x-3 gap-y-1.5 px-8 py-2 " +
  "max-[900px]:flex-wrap max-[900px]:px-4";

export function Shell({ children }: { children: ReactNode }) {
  const { me, signOut } = useSession();
  const { current } = useCurrentAgent();
  const internal = me?.user.role === "internal";
  const to = useScopedTo();
  const now = useClock();
  const tz = current?.timezone;
  const bar = useRef<HTMLElement>(null);
  // The one route laid out by a setting. See this file's header.
  const register = useMatch("/:agent/calls/*") !== null && current?.view === "simple";

  useLayoutEffect(() => {
    const node = bar.current;
    if (!node) return;
    // ON THE ROOT, like `PageHead`'s own height and for the same reason: the elements that
    // read it - the page head, the calls log's day headings, the simplified register's
    // height - are nowhere near being descendants of this header.
    const publish = () =>
      document.documentElement.style.setProperty("--spacing-bar", `${node.offsetHeight}px`);
    publish();
    // Measured rather than assumed, because under 900px it is two rows and above it is one,
    // and a business name long enough to wrap makes it two rows on a tablet too.
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex min-h-full flex-col">
      <header ref={bar} className={BAR}>
        <div className={BAR_ROW}>
          <NavLink to={current ? to("/") : "/"} className="flex flex-none items-center" title="Overview">
            <Mark className="text-accent" />
            <span className="sr-only">Overview</span>
          </NavLink>
          <AgentSwitcher internal={internal} />

          {current && (
            <>
              {/* A RULE, NOT A GAP, between whose dashboard this is and where you are in it. */}
              <span aria-hidden="true" className="mx-1 h-6 w-px flex-none bg-line max-[900px]:hidden" />

              <nav
                aria-label="Pages"
                className="flex items-center gap-1 max-[900px]:order-last max-[900px]:w-full"
              >
                <BarLink to={to("/")} end icon={<Gauge />} label="Overview" />
                <BarLink to={to("/calls")} icon={<Phone />} label="Calls" />
                <BarLink to={to("/contacts")} icon={<User />} label="Contacts" />
              </nav>
            </>
          )}

          <div className="ml-auto flex flex-none items-center gap-3">
            {/* THE BUSINESS'S CLOCK. The date is the first thing to go on a narrow screen;
                the time stays, because "is it still open" is asked of it at any width. */}
            <p className="nums flex items-baseline gap-2 text-sm whitespace-nowrap text-ink-soft">
              <span className="max-[1100px]:hidden">{clockDate(now, tz)}</span>
              <time className="font-[550] text-ink">{timeOfDay(now, tz)}</time>
            </p>

            {current && (
            <NavLink
              to={to("/settings")}
              title="Settings"
              className={({ isActive }) =>
                cx(
                  "grid size-10 place-items-center rounded-sm transition-colors",
                  isActive
                    ? "bg-sunken text-ink"
                    : "text-ink-faint hover:bg-line-soft hover:text-ink",
                )
              }
            >
              <Cog size={20} />
              <span className="sr-only">Settings</span>
            </NavLink>
            )}

            {/* THE ACCOUNT IS ONE AVATAR, and what is behind it is a list. See `UserMenu`. */}
            <UserMenu
              avatar={<Avatar>{initials(me?.user.name ?? me?.user.email ?? "?")}</Avatar>}
              name={me?.user.name ?? me?.user.email ?? ""}
              detail={[me?.user.name ? me.user.email : null, internal ? "Internal" : me?.organization?.name]
                .filter(Boolean)
                .join(" · ")}
              items={[
                ...(current
                  ? [{ kind: "link" as const, label: "Team", icon: <User size={16} />, to: to("/settings?tab=team") }]
                  : []),
                {
                  kind: "action",
                  label: "Sign out",
                  icon: <SignOut size={16} />,
                  onSelect: () => void signOut(),
                  danger: true,
                },
              ]}
            />
          </div>
        </div>
      </header>

      {register ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        <div className="mx-auto w-full max-w-[1560px] px-8 pt-6.5 pb-16 max-[900px]:px-4 max-[900px]:pt-4.5 max-[900px]:pb-12">
          {children}
        </div>
      )}
    </div>
  );
}

export function Avatar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "grid size-7 flex-none place-items-center rounded-full bg-accent",
        "text-2xs font-[650] tracking-[0.02em] text-accent-ink",
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * The selected page is a filled pill, and that is enough on its own.
 *
 * `sunken` carrying `ink`, 15.4:1, on a white bar. It used to need a 3px indigo marker
 * beside it when the pill was a pale `accent-soft` wash that was easy to miss down a tall
 * rail; this fill answers "where am I" without one, and a marker next to it would be a
 * second answer to a question already answered.
 *
 * The item stays neutral rather than going indigo because navigation is chrome, and
 * chrome in this product is ink. See `BUTTON_VARIANTS` in `ui.tsx`.
 */
function BarLink({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cx(
          "flex items-center gap-2 rounded-sm px-3 py-[8px] text-base font-medium transition-colors",
          "[&_svg]:flex-none",
          isActive
            ? "bg-sunken font-[550] text-ink [&_svg]:text-current"
            : "text-ink-soft [&_svg]:text-ink-faint hover:bg-line-soft hover:text-ink hover:[&_svg]:text-current",
        )
      }
    >
      {icon}
      {label}
    </NavLink>
  );
}

/**
 * Which agent this dashboard is showing.
 *
 * A NAME WHEN THERE IS ONE AND A CONTROL WHEN THERE ARE SEVERAL, which is a decision about
 * the data and not about the reader's role. Most owners have one agent and get its name
 * stated - the name being the agent's rather than the organization's. An internal reader looking
 * at a database with one agent in it is in exactly that position and gets the same name:
 * with no fleet-wide view left to switch to, a menu of one is not a control.
 *
 * THE ORGANIZATION'S NAME ONLY WHERE IT DISAMBIGUATES. An internal reader is looking at
 * several organizations' agents and needs to know whose is whose; an owner reading their own
 * two agents does not need their own organization's name printed twice. The slug rides along as a
 * search key either way, which is how the one identifier that is stable across renames
 * stays findable without being on screen.
 */
function AgentSwitcher({ internal }: { internal: boolean }) {
  const { agents, current } = useCurrentAgent();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  /*
   * THE NAME IS THE CARD NOW. In the rail this was a bordered box with a label
   * over the name, which was room the rail had and a bar does not. What survives is the
   * part that did the work: the business's own name, set larger than anything else in the
   * bar, so the question "whose dashboard is this" is answered before anything else is read.
   */
  const name =
    "flex min-w-0 max-w-[28ch] items-center gap-1.5 text-lg font-[650] tracking-[-0.018em] text-ink";

  // One agent - or none yet - is a statement rather than a menu of one. An owner in
  // that position never learns the word "agent" from this bar, which is most of them.
  // OUR TEAM ALWAYS GETS THE MENU, because "New agent" lives at its foot, and a database with
  // no agent in it is exactly when that is needed.
  if (agents.length <= 1 && !internal) {
    return (
      <span className={name}>
        <span className="truncate">{current?.name ?? "Voice Agents"}</span>
      </span>
    );
  }

  const choices: Choice[] = agents.map((row) => ({
    value: row.slug,
    label: row.name,
    // Both provisioning paths default an agent's name to its slug, so most rows would
    // otherwise print the same string twice.
    note: internal ? row.organizationName : row.slug === row.name ? undefined : row.slug,
    keywords: [row.slug, row.organizationName],
  }));

  return (
    <Listbox
      value={current?.slug ?? null}
      choices={choices}
      ariaLabel="Choose an agent"
      className={cx(name, "cursor-pointer rounded-sm px-2 py-1 text-left hover:bg-line-soft")}
      // Settings may hold a draft; `confirmLeave` asks before a switch throws it away.
      onChange={(next) => {
        if (confirmLeave()) navigate(current ? switchedTo(pathname, search, next) : `/${next}`);
      }}
      action={
        internal
          ? {
              label: "New agent",
              icon: <Plus size={16} />,
              onSelect: () => {
                if (confirmLeave()) navigate("/agents/new");
              },
            }
          : undefined
      }
    >
      <span className="truncate">{current?.name ?? "Voice Agents"}</span>
      <ChevronDown size={16} className="flex-none text-ink-faint" />
    </Listbox>
  );
}

/** The pages that exist for every agent, and so survive a switch. */
const SECTIONS: ReadonlySet<string> = new Set(["calls", "contacts", "settings"]);

/**
 * Where choosing another agent lands: the same section, at its top.
 *
 * `/<agent>/<section>/...` BECOMES `/<next>/<section>`, and the rest of the path is cut,
 * because anything under a section names a thing that belongs to the agent being left:
 * `/autofix-voice/calls/67` is one of Auto Fix's calls, and keeping `67` under Hydrofoil would
 * open Auto Fix's call under Hydrofoil's name. The Worker would serve it (a call id is scoped
 * by organization, not by agent), which is exactly why the dashboard has to stop asking. A
 * page that is not a section - `/build`, which is one agent's build - lands on the overview.
 *
 * The query survives except the cursor and the contact. A new agent is a new list, and a
 * stale cursor reads as "this agent has no calls"; a filter or a tab still means the same thing.
 */
export function switchedTo(pathname: string, search: string, slug: string): string {
  const section = pathname.split("/")[2] ?? "";
  const params = new URLSearchParams(search);
  params.delete("cursor");
  // A contact is one agent's: the calls of Mark-at-the-boat-shop filtered onto the dental
  // practice's list would be an empty page that looks like a bug.
  params.delete("contact");
  const query = params.toString();
  return `/${slug}${SECTIONS.has(section) ? `/${section}` : ""}${query ? `?${query}` : ""}`;
}

/** `Alex Kim` reads as AK; an email falls back to its first letters. */
export function initials(value: string): string {
  const name = value.includes("@") ? value.split("@")[0]! : value;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
